<template>
  <!-- ── Loading screen — shown while session check is in flight ────────────── -->
  <div
    v-if="sessionChecking"
    class="fixed inset-0 bg-gray-900 flex flex-col items-center justify-center z-50"
  >
    <div class="w-10 h-10 rounded-full border-2 border-gray-700 border-t-blue-500 animate-spin mb-5" />
    <span class="text-gray-500 text-xs tracking-[0.25em] uppercase">Check-In</span>
  </div>

  <!-- ── IDE — mounted only after session check completes ──────────────────── -->
  <div v-else class="flex flex-col h-screen bg-gray-900 text-gray-100 overflow-hidden">

    <!-- Command Palette — global overlay, rendered via <Teleport to="body"> -->
    <CommandPalette v-model="commandPaletteOpen" @switch-panel="handleActivate" />

    <!-- ── Main layout ────────────────────────────────────────────────────── -->
    <!--
      Wireframe column order:
        [Activity Bar 48 px] | [Sidebar 256 px] | [Editor + bottom panel]
    -->
    <div class="flex flex-1 min-h-0 overflow-hidden">

      <!-- Activity Bar: repo icons (top) + tool icons (bottom) -->
      <ActivityBar
        :active-panel="activePanel"
        @activate="handleActivate"
      />

      <!-- Sidebar: content switches based on active tool icon -->
      <div class="w-64 shrink-0 border-r border-gray-700 overflow-hidden flex flex-col">
        <FileExplorer  v-if="activePanel === 'explorer'" />
        <GitPanel      v-else-if="activePanel === 'git'" />
        <ClaudeSidebar v-else-if="activePanel === 'claude'" />
        <!-- Repos panel: fallback for when there are more repos than activity-bar slots -->
        <RepoSidebar   v-else-if="activePanel === 'repos'" />
        <!-- NAS repository manager: add / open / pull / delete cloned repos -->
        <RepoManager   v-else-if="activePanel === 'nas-repos'" @open-explorer="handleActivate('explorer')" />
      </div>

      <!-- Editor area + resizable bottom panel ──────────────────────────── -->
      <!--
        SplitPane keeps EditorPane always mounted so Monaco is never destroyed
        when the bottom panel toggles. The second slot renders the terminal (or
        a hint) without touching the first slot at all.
      -->
      <SplitPane
        direction="vertical"
        fixed-pane="second"
        class="flex-1 min-w-0"
        :initial-size="initialTerminalHeight"
        :min-first="120"
        :min-second="60"
        :max-fixed="maxTerminalHeight"
        @resize="onTerminalResize"
      >
        <template #first>
          <EditorPane />
        </template>

        <template #second>
          <TerminalPanel v-if="terminalStore.isOpen" />
          <div
            v-else
            class="h-full flex items-center justify-center text-gray-600 text-xs select-none"
          >
            Press
            <kbd class="mx-1.5 px-1.5 py-0.5 rounded bg-gray-800 border border-gray-600 text-gray-400 font-mono text-[10px]">
              Ctrl+`
            </kbd>
            to open terminal
          </div>
        </template>
      </SplitPane>

    </div>

    <!-- Status Bar (always visible, anchored to bottom) -->
    <StatusBar @open-branch-switcher="handleActivate('git')" />

  </div>
  <!-- end IDE wrapper -->

</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useEventListener } from '@vueuse/core'

import ActivityBar    from '@/components/ActivityBar.vue'
import CommandPalette from '@/components/CommandPalette.vue'
import SplitPane      from '@/components/SplitPane.vue'
import StatusBar      from '@/components/StatusBar.vue'

import EditorPane     from '@/editor/EditorPane.vue'
import FileExplorer   from '@/explorer/FileExplorer.vue'
import GitPanel       from '@/git/GitPanel.vue'
import ClaudeSidebar  from '@/claude/ClaudeSidebar.vue'
import TerminalPanel  from '@/terminal/TerminalPanel.vue'
import RepoSidebar    from '@/repositories/RepoSidebar.vue'
import RepoManager    from '@/repositories/RepoManager.vue'

import { useRepoStore }     from '@/stores/repoStore'
import { useTerminalStore } from '@/stores/terminalStore'
import { useGitStore }      from '@/stores/gitStore'
import axios from 'axios'
import apiClient, { setCsrfToken } from '@/services/apiClient'
import { connectSocket } from '@/websocket/wsClient'

const repoStore     = useRepoStore()
const terminalStore = useTerminalStore()
const gitStore      = useGitStore()

// ── Layout state ─────────────────────────────────────────────────────────────

/** True while the /api/ide/session check is in flight. Hides the IDE until resolved. */
const sessionChecking = ref(true)

/** Which sidebar panel is currently visible. */
const activePanel = ref<string>('explorer')

/** Whether the Command Palette overlay is open. */
const commandPaletteOpen = ref(false)

/** Terminal initial height: 240 px (roughly 3–4 terminal rows). */
const initialTerminalHeight = ref(240)

/** Terminal max height: 50 % of the current viewport height. */
const maxTerminalHeight = ref(Math.round(window.innerHeight * 0.5))

function onTerminalResize(_size: number): void { /* reserved for persistence */ }

function updateMaxTerminalHeight(): void {
  maxTerminalHeight.value = Math.round(window.innerHeight * 0.5)
}

// ── Panel activation ──────────────────────────────────────────────────────────

/**
 * Handle a panel activation event from ActivityBar.
 *
 * 'terminal' is special: it toggles the bottom terminal panel rather than
 * switching the sidebar content. All other panel IDs replace the sidebar.
 */
function handleActivate(panel: string): void {
  if (panel === 'terminal') {
    if (terminalStore.tabs.length === 0) {
      // activeRepoPath is the absolute server path (repo.path ?? repo.name).
      // The terminal server validates CWD against ALLOWED_REPO_PATHS (absolute paths).
      // activeRepoRootPath is the workspace-relative name used only by the PHP file API.
      terminalStore.openTerminal(repoStore.activeRepoPath || '/')
    } else {
      terminalStore.togglePanel()
    }
    return
  }
  activePanel.value = panel
}

// ── Keyboard shortcuts ────────────────────────────────────────────────────────

useEventListener('keydown', (e: KeyboardEvent) => {
  const mod = e.ctrlKey || e.metaKey

  // Cmd/Ctrl+Shift+P → Command Palette
  if (mod && e.shiftKey && e.key === 'P') {
    e.preventDefault()
    commandPaletteOpen.value = !commandPaletteOpen.value
  }

  // Ctrl+` → toggle terminal panel (backtick, key code 192)
  if (mod && e.key === '`') {
    e.preventDefault()
    handleActivate('terminal')
  }
})

// ── Bootstrap ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  window.addEventListener('resize', updateMaxTerminalHeight)
  // Step 1: obtain CSRF + WebSocket session tokens from Craft before anything
  // else runs. This must complete before any mutating API call or WS connect.
  try {
    const { data } = await apiClient.get<{ csrfToken: string; sessionToken: string; userName: string; userId: number; userEmail: string }>(
      '/api/ide/session'
    )
    setCsrfToken(data.csrfToken)
    window.__CRAFT_SESSION_TOKEN__ = data.sessionToken
    repoStore.currentUser      = data.userName
    repoStore.currentUserId    = data.userId
    repoStore.currentUserEmail = data.userEmail
    connectSocket()
  } catch (err) {
    if (axios.isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
      window.location.href = 'https://evelyn.website-to-go.com'
      return  // keep loading screen visible while redirect happens
    }
    console.error('[App] session init failed — WS and mutating APIs will not work:', err)
  }

  onUnmounted(() => { window.removeEventListener('resize', updateMaxTerminalHeight) })
  sessionChecking.value = false

  // Step 2: load repositories (filesystem repos + user's cloned NAS repos in parallel).
  await Promise.all([
    repoStore.fetchRepositories(),
    repoStore.fetchUserRepos(),
  ])

  // Step 3: pre-fetch git status so the status bar branch name appears
  // immediately without requiring the user to open the git panel first.
  if (repoStore.activeRepoPath) {
    gitStore.fetchStatus(repoStore.activeRepoPath)
  }
})
</script>
